import React, { useEffect, useState } from "react";
import "./emrEmulator.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { defaultEmrValues } from "./emulatorConstant";
import useAdmin from "../../Hooks/useAdmin";
import Loading from "../../components/LoadingPage/Loading";

const inputWithButtonFields = ["serverDate", "serverTime", "encounterDate", "emrOrderDate"];

const EmrEmulator = () => {
    const [values, setValues] = useState<Record<string, any>>(defaultEmrValues);
    const [rxInfoResponse, setRxInfoResponse] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const { getTenantKeyAPI, getTenantKeyLoading } = useAdmin();
    const baseUrl = useSelector((state: RootState) => state.config.configData?.appApiBaseUrl);

    useEffect(() => {
        const profileClientId = localStorage.getItem("profileClientId");
        if (baseUrl && profileClientId) {
            setValues((prev) => ({
                ...prev,
                wsUrl: `${baseUrl}/recordusage/getbarcodeinfo?tenantId=${profileClientId}`,
            }));
        }
    }, [baseUrl]);

    useEffect(() => {
        (async () => {
            try {
                const tenantRes = await getTenantKeyAPI({});
                const settingValue = tenantRes?.[0]?.settingValue;
                if (settingValue) {
                    setValues((prev) => ({ ...prev, accessKey: settingValue }));
                }
            } catch (error) {
                console.error("Failed to fetch tenant key:", error);
            }
        })();
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setValues(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const getDate = () => {
        const currentTime = new Date();
        const month = currentTime.getMonth() + 1;
        const day = currentTime.getDate();
        const year = currentTime.getFullYear();
        return `${month}/${day}/${year}`;
    };

    const getTime = () => {
        const currentTime = new Date();
        let hours = currentTime.getHours();
        let minutes: any = currentTime.getMinutes();
        let outString;

        if (minutes < 10) {
            minutes = "0" + minutes;
        }

        outString = hours + ":" + minutes + " ";
        if (hours > 11) {
            outString += "PM";
        } else {
            outString += "AM";
        }

        return outString;
    };

    const handleSetDateTime = () => {
        setValues(prev => ({
            ...prev,
            serverDate: getDate(),
            serverTime: getTime()
        }));
    };

    const handleFieldButtonClick = (fieldName: string) => {
        if (!fieldName) return;
        const isTime = fieldName.toLowerCase().includes("time");
        setValues((prev) => ({
            ...prev,
            [fieldName]: isTime ? getTime() : getDate(),
        }));
    };

    const getBarcodeInfo = async () => {
        const { wsUrl, ...rest } = values;
        setLoading(true);
        try {
            const response = await fetch(wsUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(rest),
            });

            const data = await response.json();
            if (!response.ok) throw data;

            setRxInfoResponse((prev) => [...prev, data]);
        } catch (error: any) {
            const errorObj = typeof error === "object" && error !== null ? error : { message: String(error) };
            setRxInfoResponse((prev) => [...prev, { error: errorObj }]);
        } finally {
            setLoading(false);
        }
    };

    const clearRxInfoResponse = () => setRxInfoResponse([]);

    return (
        <>
            {getTenantKeyLoading || loading && (<Loading />)}
            <main className="emr-emulator-main p-4">
                <header className="header-main mb-3">
                    <h3>
                        <strong>SRX- EMR Emulator</strong>
                    </h3>
                    <div className="actions-wrapper">
                        <button className="btn btn-info" onClick={getBarcodeInfo}>Get Barcode Info</button>
                        <button className="btn btn-info ml-2" onClick={clearRxInfoResponse}>Clear</button>
                        <button className="btn btn-info ml-2" onClick={handleSetDateTime}>Set Date/Time</button>
                    </div>
                </header>

                <div className="p-3 bg-white mb-4 border response-box">
                    <span className="mb-2 d-inline-block">Response:</span>
                    {rxInfoResponse.length > 0 ? (
                        rxInfoResponse.map((response, index) => (
                            <pre key={index} className="json-output">
                                {JSON.stringify(response, null, 4)}
                            </pre>
                        ))
                    ) : (
                        <p className="text-muted">No responses yet.</p>
                    )}
                </div>

                <div className="row g-2 p-2 m-0 border bg-white emr-form-main">
                    {Object.keys(defaultEmrValues).map((key) => (
                        <div
                            key={key}
                            className={`${key === "wsUrl" ? "col-12" : "col-4"} p-2 m-0`}
                        >
                            <label className="mb-1" htmlFor={`${key}_input`}>
                                {key}:
                            </label>
                            {inputWithButtonFields.includes(key) ? (
                                <div className="input-with-button">
                                    <input
                                        id={`${key}_input`}
                                        name={key}
                                        type="text"
                                        className="form-control"
                                        value={values?.[key] ?? ""}
                                        onChange={handleInputChange}
                                    />
                                    <button
                                        className="ml-2"
                                        type="button"
                                        onClick={() => handleFieldButtonClick(key)}
                                    >
                                        {key.toLowerCase().includes("time") ? "Now" : "Today"}
                                    </button>
                                </div>
                            ) : (
                                <input
                                    id={`${key}_input`}
                                    name={key}
                                    type="text"
                                    className="form-control"
                                    value={values?.[key] ?? ""}
                                    onChange={handleInputChange}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </main>
        </>
    );
};

export default EmrEmulator;